'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCircleCheck,
  faDiamondTurnRight,
  faPencil,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { twMerge } from 'tailwind-merge';
import { PulseIconButton } from 'src/components/PulseUI/PulseIconButton';
import { useGetRouteStopsQuery } from 'src/api/redux';
import type { Route } from 'src/api/redux/routes';
import { useAppSelector } from 'src/redux/hooks';
import { selectRouteSession } from 'src/redux/slices/routeNavigationSlice';
import { getNextUnvisitedStop, getStopKey } from 'src/hooks/routes/utils';
import {
  Card,
  CardContent,
  CardTitle,
} from 'src/components/Routes/components/ui';

export interface RouteRowProps {
  route: Route;
  isVisible: boolean;
  onView: (routeId: number) => void;
  onEdit: (routeId: number) => void;
  onDelete: (routeId: number) => void;
  onToggleVisibility: (routeId: number) => void;
}

const RouteRow: React.FC<RouteRowProps> = ({
  route,
  isVisible: _isVisible,
  onView,
  onEdit,
  onDelete,
  onToggleVisibility: _onToggleVisibility,
}) => {
  const stopCount = route.stop_count ?? 0;
  const session = useAppSelector((state) => selectRouteSession(state, route.id));

  // Only routes with visit progress need their stops, to name the active/next stop
  const { data: stops } = useGetRouteStopsQuery(String(route.id), {
    skip: !session,
  });

  const visitedStopKeys = session?.visitedStopKeys ?? [];
  // Count against the loaded stops so keys of removed stops don't overstate progress
  const visitedCount = stops
    ? stops.filter((stop) => visitedStopKeys.includes(getStopKey(stop))).length
    : Math.min(visitedStopKeys.length, stopCount);
  const isComplete = !!session && stopCount > 0 && visitedCount === stopCount;

  const activeStopName =
    session?.currentStopKey && !visitedStopKeys.includes(session.currentStopKey)
      ? stops?.find((stop) => getStopKey(stop) === session.currentStopKey)?.name
      : null;
  const nextUpName =
    stops && stops.length > 0
      ? getNextUnvisitedStop(stops, visitedStopKeys)?.name
      : null;

  return (
    <Card
      className={twMerge(
        'group cursor-pointer transition-colors duration-200 hover:border-sky-300',
        isComplete && 'border-green-500'
      )}
      onClick={() => onView(route.id)}
      role="button"
      tabIndex={0}
      aria-label={`View route: ${route.label}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onView(route.id);
        }
      }}
      data-testid={`route-row-${route.id}`}
    >
      <CardContent className="gap-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base truncate">{route.label}</CardTitle>
          <div className="flex items-center gap-3 shrink-0">
            <div
              className="hidden group-hover:flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {/* TODO: Re-enable visibility toggle when map display is implemented */}
              <PulseIconButton
                variant="plain"
                color="neutral"
                size="sm"
                icon={faPencil}
                onClick={() => onEdit(route.id)}
                aria-label="Edit route"
                tooltip="Edit route"
                data-testid={`route-edit-button-${route.id}`}
              />
              <PulseIconButton
                variant="plain"
                color="danger"
                size="sm"
                icon={faTrash}
                onClick={() => onDelete(route.id)}
                aria-label="Delete route"
                tooltip="Delete route"
                data-testid={`route-delete-button-${route.id}`}
              />
            </div>
            <span
              className="text-sm text-neutral-700"
              data-testid={`route-row-progress-${route.id}`}
            >
              {session ? (
                <>
                  <strong>
                    {visitedCount} / {stopCount}
                  </strong>{' '}
                  Visited
                </>
              ) : (
                `${stopCount} ${stopCount === 1 ? 'stop' : 'stops'}`
              )}
            </span>
            <Icon icon={faArrowRight} className="text-neutral-800" />
          </div>
        </div>

        {isComplete ? (
          <div
            className="flex items-center gap-2 text-sm font-semibold text-neutral-800"
            data-testid={`route-row-status-${route.id}`}
          >
            <Icon icon={faCircleCheck} className="text-green-600" />
            Route Complete
          </div>
        ) : activeStopName ? (
          <div
            className="flex items-center gap-2 text-sm text-neutral-500"
            data-testid={`route-row-status-${route.id}`}
          >
            <Icon icon={faDiamondTurnRight} className="text-sky-600" />
            <span className="truncate">Active Stop: {activeStopName}</span>
          </div>
        ) : nextUpName ? (
          <div
            className="flex items-center gap-2 text-sm text-neutral-500"
            data-testid={`route-row-status-${route.id}`}
          >
            <Icon icon={faDiamondTurnRight} className="text-green-600" />
            <span className="truncate">Next Up: {nextUpName}</span>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default RouteRow;
